<template>
  <!-- If property type is residential we treat it as a card -->
  <validation-observer
    :id="`form-${index}`"
    ref="inspection-form"
    tag="div"
    class="inspection-form"
    :class="{ 'error': !isValidPromise && isValidPromise !== null }"
  >
    <div class="p-lg">
      <div
        v-if="!form.fixingInspection"
        class="field content"
      >
        <div
          v-if="isResidential"
          class="control validate-tooltip"
        >
          <h3
            v-if="!isNameEditable"
            class="m-b-lg"
          >
            {{ form.name }} 
            <help-icon
              v-show="String(form.name).toLowerCase() === 'single unit'"
              modal="single-unit"
            />
          </h3>
          <validation-provider
            v-else
            v-slot="{ errors, reset }"
            rules="required|correctUnit|uniqueUnit"
            name="Unit Name"
            mode="lazy"
          >
            <unit-name
              v-model="form.name"
            />
            <transition name="fade">
              <span
                v-if="errors[0]"
                class="cell shrink error popover"
                tabindex="0"
                @click="reset()"
                v-text="errors[0]"
              />
            </transition>
          </validation-provider>
        </div>
        <div
          v-else
          class="field"
        >
          <div class="control">
            <label class="label">
              <span class="is-block p-b-sm">Business name</span>
              <textbox-with-validation
                v-model="form.name"
                rules="required"
                name="name"
                placeholder="Business name"
                required
              />
              <small class="has-text-weight-light" />
            </label>
          </div>
        </div>
      </div>
      <div
        v-else
        class="content"
      >
        <h3
          class=""
        >
          <status-tag
            :status="form.orgStatus"
            class="is-vertical-align-middle"
          />
          <span class="is-vertical-align-middle">
            {{ form.name }}
          </span>
        </h3>
      </div>
    
      <div class="field">
        <div class="control is-expanded">
          <p class="label">
            Inspection type:
            <help-icon modal="insp_type" />
          </p>
          <dropdown-with-validation
            v-model="form.inspectionType"
            name="inspectionType"
            placeholder="Inspection type"
            :default-option="`- Inspection type -`"
            :options="{ 'LeadFree': 'Lead Free', 'LeadSafe': 'Lead Safe' }"
            rules="required"
            required
          />
        </div>
      </div>
      
      <div class="field is-horizontal">
        <div class="field-body field-grouped-half-fix">
          <div class="field">
            <div class="control is-expanded">
              <p class="label">
                Risk Assessor, Lead Inspector PA Lic. # or EPA Firm Certification #:
                <help-icon modal="palicense" />
              </p>
              <textbox-with-validation
                v-model="form.licenseOrTechnicianNumber"
                rules="required"
                name="licenseOrTechnicianNumber"
                placeholder="Type #"
                required
              />
            </div>
          </div>

          <div class="field">
            <div class="control is-expanded validate-tooltip">
              <label
                for="date-picker-date"
                class="label"
              >
                Sampling date:
                <help-icon modal="sampling_date" />
              </label>
              <validation-provider
                v-slot="{ errors, reset }"
                :rules="samplingDateValidation"
                :vid="`inspection-date-date-picker-date`"
                name="Sampling date"
                mode="eager"
              >
                <div class="input-wrap input-text required">
                  <datepicker
                    id="date-picker-date"
                    v-model="localDate"
                    :disabled-dates="disabledDates"
                    format="yyyy-MM-dd"
                    placeholder="Sampling date"
                    name="samplingDate"
                    input-class="input"
                  />
                </div>
                <transition name="fade">
                  <span
                    v-if="errors[0]"
                    class="cell shrink error popover"
                    tabindex="0"
                    @click="reset()"
                    v-text="errors[0]"
                  />
                </transition>
              </validation-provider>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="form.inspectionType === 'LeadSafe'"
        class="field"
      >
        <div class="field-body field-grouped-half-fix">
          <div class="field">
            <div class="control is-expanded">
              <p class="label">
                Laboratory name:
                <help-icon modal="lab_name" />
              </p>
              <textbox-with-validation
                v-model="form.labAccreditationName"
                name="labAccreditationName"
                rules="required"
                placeholder="Laboratory name"
                required
              />
            </div>
          </div>
          <div class="field">
            <div class="control is-expanded">
              <p class="label">
                Lab Accreditation Number:
                <help-icon modal="acc_number" />
              </p>
              <textbox-with-validation
                v-model="form.labAccreditationNumber"
                name="labAccreditationNumber"
                rules="required"
                placeholder="Accreditation number"
                required
              />
            </div>
          </div>
        </div>
      </div>

      <template v-if="form.inspectionType === 'LeadSafe'">
        <hr>
        <div class="content">
          <h4 class="">
            Dust wipe samples
            <help-icon modal="dustwipefigures" />
          </h4>
        </div>
        <!-- For residential I have to load the initial 5, for family it is only 3 -->
        <div
          v-for="(figure, ind) in form.dustWipeFigures"
          :key="ind"
        >
          <div class="m-t-md validate-tooltip">
            <validation-provider
              :id="`figure-${ind}`"
              v-slot="{ errors, reset }"
              :vid="`figure-${ind}`"
              :name="`figure-${ind}`"
              rules="validFigure"
              mode="eager"
            >
              <dust-wipe-figure
                v-model="form.dustWipeFigures[ind]"
                :index="ind"
                :property-type="details.propertyType"
                class="is-danger"
              />
              <transition name="fade">
                <span
                  v-if="errors[0]"
                  class="cell shrink error popover"
                  tabindex="0"
                  @click="reset()"
                  v-text="errors[0]"
                />
              </transition>
            </validation-provider>
          </div>

          <template v-if="ind == (threshold - 1)">
            <hr>
            <div class="content">
              <p
                v-if="isResidential"
                v-html="$wording.certify.text_4"
              />
              <p
                v-else
                v-html="$wording.certify.text_6"
              />
            </div>
            <div
              class="field"
            >
              <div class="control">
                <p class="label">
                  Number of {{ roomsTypes }}
                </p>
                <dropdown-with-validation
                  id="renderNumberOfBedrooms"
                  v-model="renderNumberOfBedrooms"
                  :default-option="`- Number of ${roomsTypes} -`"
                  :options="{ 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9, 10: 10 }"
                  name="renderNumberOfBedrooms"
                  :placeholder="`Number of ${roomsTypes}`"
                  rules="required"
                  class="number-of-bedrooms"
                  required
                />
              </div>
            </div>
          </template>
        </div>

        <!-- <div
          v-if="(form.dustWipeFigures.length > threshold) && !!numberOfBedrooms"
          class="field m-t-md"
        >
          <div class="control has-text-right">
            <button
              class="button is-small is-danger remove-last-figure"
              @click="remove()"
            >
              <span class="icon">
                <i
                  class="fas fa-trash"
                  aria-hidden="true"
                />
              </span>
              <span v-text="`Remove last`" />
            </button>
          </div>
        </div> -->

        <!-- <div
          v-if="(isResidential && form.dustWipeFigures.length < 21) && !!numberOfBedrooms"
          class="field"
        >
          <hr>
          <div class="control has-text-centered">
            <div class="dropdown is-hoverable">
              <div class="dropdown-trigger">
                <button
                  class="button is-light add-bedroom"
                  aria-haspopup="true"
                  aria-controls="dropdown-add-bedroom"
                >
                  <span>Add bedroom</span>
                  <span class="icon is-small">
                    <i
                      class="fas fa-angle-down"
                      aria-hidden="true"
                    />
                  </span>
                </button>
              </div>
              <div
                id="dropdown-add-bedroom"
                class="dropdown-menu"
                role="menu"
              >
                <div class="dropdown-content">
                  <a
                    class="dropdown-item add-bedroom-front"
                    role="menuitem"
                    @click.prevent="addRoom('front')"
                  >
                    Front
                  </a>
                  <a
                    class="dropdown-item add-bedroom-middle"
                    role="menuitem"
                    @click.prevent="addRoom('middle')"
                  >
                    Middle
                  </a>
                  <a
                    class="dropdown-item add-bedroom-rear"
                    role="menuitem"
                    @click.prevent="addRoom('rear')"
                  >
                    Rear
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div> -->
        <!-- <div
          v-else-if="(!isResidential && form.dustWipeFigures.length < 21) && !!numberOfBedrooms"
          class="field"
        >
          <hr>
          <div class="control has-text-centered">
            <button
              class="button is-light add-common-area"
              @click="addRoom('')"
            >
              Add common area
            </button>
          </div>
        </div> -->
      </template>
    </div>

    <div class="p-lg has-background-ghost-grey">
      <div class="content">
        <h4>Files: <help-icon modal="files" /></h4>
      </div>
      <div
        ref="report-tooltip"
        :data-tippy-content="$wording.certify.files_tooltip"
        class="field"
      >
        <uploader
          ref="upload-report"
          v-model="form.report"
          :url="uploadFileURL"
          :request-parameters-controller="requestReportController"
          :response-parameters-controller="responseReportController"
          :remove-guard="removeGuard"
          class="m-b-xs"
          @reset="reportFile = null"
        />
        <div class="control">
          <div class="control is-expanded validate-tooltip">
            <validation-provider
              v-slot="{ errors, reset }"
              :rules="isReportRequired"
              name="Report file"
              :vid="`report-file`"
              mode="passive"
            >
              <file
                v-show="!form.report"
                v-model="reportFile"
                :disabled="!form.name"
                placeholder="Attach report"
                name="reportFile"
                title="Choose file"
              >
                <p><small v-html="$wording.certify.report_tooltip" /></p>
              </file>
              <transition name="fade">
                <span
                  v-if="errors[0]"
                  class="cell shrink error popover"
                  tabindex="0"
                  @click="reset()"
                  v-text="errors[0]"
                />
              </transition>
            </validation-provider>
          </div>
        </div>
      </div>

      <div
        ref="certificate-tooltip"
        :data-tippy-content="$wording.certify.files_tooltip"
        class="field"
      >
        <div
          v-for="(certificate, indx) in form.certificates"
          :key="indx"
        >
          <!-- TODO: Review the input function, there must be a way to do it better. -->
          <uploader
            :ref="`upload-certificate`"
            :value="getCertificateFileName(certificate)"
            :url="uploadFileURL"
            :request-parameters-controller="requestCertificateController"
            :response-parameters-controller="responseCertificateController"
            :remove-guard="removeGuard"
            class="m-b-xs"
            @input="value => setCertificateFileName(value, indx)"
            @reset="certificateFile = null"
          />
        </div>
        <div class="control validate-tooltip">
          <validation-provider
            v-slot="{ errors, reset }"
            :rules="isCertificateRequired"
            name="Certificate file"
            :vid="`Certificate-file`"
            mode="passive"
          >
            <file
              v-if="!(!isAdmin && form.certificates.length > 0)"
              ref="attach-certificate"
              v-model="certificateFile"
              :disabled="!form.name"
              placeholder="Attach certificate"
              name="certificateFile"
              title="Choose file"
            >
              <p><small v-html="$wording.certify.certificate_tooltip" /></p>
            </file>
            <transition name="fade">
              <span
                v-if="errors[0]"
                class="cell shrink error popover"
                tabindex="0"
                @click="reset()"
                v-text="errors[0]"
              />
            </transition>
          </validation-provider>
        </div>
      </div>
    </div>
  </validation-observer>
</template>

<script>
// import tippy from 'tippy.js';

import DustWipeFigure from './DustWipeFigure';
import File from './File';
import Uploader from './Uploader';
import UnitName from './UnitName';
import HelpIcon from '@/components/common/HelpIcon';

import DropdownWithValidation from "@/components/common/DropdownWithValidation";
import TextboxWithValidation from "@/components/common/InputTextWithValidation";

import tippy from 'tippy.js';
import Datepicker from 'vuejs-datepicker';
import { EventBus } from '@/plugins/bus';

export default {
  name: "Unit",
  components: {
    HelpIcon,
    DustWipeFigure,
    TextboxWithValidation,
    DropdownWithValidation,
    File,
    Datepicker,
    Uploader,
    UnitName,
  },
  props: {
    index: {
      type: Number,
      required: false,
      default: 0,
    },
    isNameEditable: {
      type: Boolean,
      default: true,
    },
    details: {
      type: Object,
      required: true,
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },  
  data() {
    return {
      uploadingFiles: false,
      localDate: null,
      reportFile: null,
      certificateFile: null,
      threshold: 0,
      isVisible: !true,
      loading: false,
      isValidPromise: null,
      renderNumberOfBedrooms: null,
      numberOfBedrooms: 0,
      tempDustWipeFigures: [],
      disabledDates: {
        from: new Date(),
      },

      certificateTooltip: null,
      reportTooltip: null,
    };
  },
  computed: {
    form: {
      get() {
        return this.value;
      },
      set(form) {
        this.$emit('input', form);
      },
      cached: false,
      deep: true,
    },
    // Calculate if report is required
    isReportRequired() {
      return { required: !this.form.report, ext: [ 'pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx' ]};
    },
    samplingDateValidation() {
      return { required: true, expiredInspection: this.form.inspectionType === 'LeadSafe', date: true };
    },
    // Is certificate required
    isCertificateRequired() {
      let required = true;
      if (Array.isArray(this.form.certificates) && this.form.certificates.length) {
        required = !this.form.certificates.some(cert => !!cert.certificateAwsFileName);
      }
      return { required, ext: [ 'pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx' ]};
    },
    // Property Type and Number Of Units, these two values changes this form a lot.
    isResidential() {
      return this.details.propertyType === 'Residential';
    },
    roomsTypes() {
      return this.isResidential ? 'bedrooms' : 'common areas';
    },
    uploadFileURL() {
      if (this.isAdmin) {
        return `admin/eligibility-check/${this.details.id}/inspections/unit/files`;
      }
      return `eligibility-check/${this.details.id}/inspections/unit/files`;
    },
  },
  watch: {
    localDate(newDate) {
      let tempDate = this.$dayjs(newDate).format('YYYY-MM-DD');
      if (tempDate !== 'Invalid Date') {
        this.form.inspectionDate = tempDate;
      } else {
        this.form.inspectionDate = null;
      }
    },
    'form.inspectionType' (newType) {
      if (newType === 'LeadSafe') {
        if (this.form.dustWipeFigures.length === 0) {
          this.form.dustWipeFigures = this.$parent.$parent.newDustWipeFigures(this.details.propertyType);
          this.setNumberOfBedrooms();
          // this.renderNumberOfBedrooms = null;
        }
        if (this.form.labAccreditationName === 'N/A') {
          this.form.labAccreditationName = '';
        }
        if (this.form.labAccreditationNumber === 'N/A') {
          this.form.labAccreditationNumber = '';
        }
      }
    },
    'form.name' (newName) {
      this.toggleNameRelatedTooltips(newName);
    },
    async renderNumberOfBedrooms(newNro) {
      // Up to threshold everything should stay the same
      // Then we need to populate with the rooms and pre-populate any already entered in data.
      if (this.numberOfBedrooms > newNro) {
        if (await this.submitGuard(this.$sprintf(
          this.$wording.certify.numberOfRoomsWarning,
          this.roomsTypes,
          newNro,
          this.roomsTypes,
          this.numberOfBedrooms
        ))) {
          // this.renderNumberOfBedrooms = String(this.numberOfBedrooms);
          this.form.dustWipeFigures.splice((newNro * 2) + this.threshold, 99);
          this.setNumberOfBedrooms();
        }
        return;
      }

      for(let i = 1; i <= newNro; i += 1) {
        // Let's check if the room already exists
        if (i > this.numberOfBedrooms) {
          if (this.isResidential) {
            this.addRoom('front', i);
          } else {
            this.addRoom('', i);
          }
        }
      }
      this.setNumberOfBedrooms();
    },
  },
  beforeMount() {
    if (!this.form.dustWipeFigures) {
      return;
    }

    if (this.isResidential) {
      this.threshold = 3;
    } else {
      this.threshold = 1;
    }

    this.setNumberOfBedrooms();

    // Set initial date
    if (this.value.inspectionDate) {
      this.localDate = this.$dayjs(this.value.inspectionDate).toDate();
    } else {
      this.localDate = null;
    }

    // Set certificates to empty array if null to avoid errors
    if (!this.form.certificates) {
      this.form.certificates = [];
    }

    // Single Unit
    if (!this.isNameEditable && this.isResidential && !this.form.name) {
      this.form.name = 'Single Unit';
    }

    EventBus.$off('new-location');
    EventBus.$on('new-location', this.newLocation);
  },
  mounted() {
    // Activate tooltips
    this.$nextTick(() => {
      if (this.$refs['certificate-tooltip']) {
        this.certificateTooltip = tippy(this.$refs['certificate-tooltip'], {
          theme: 'material',
          arrow: true,
          maxWidth: 300,
        });
      }
      if (this.$refs['report-tooltip']) {
        this.reportTooltip = tippy(this.$refs['report-tooltip'], {
          theme: 'material',
          arrow: true,
          maxWidth: 300,
        });
      }

      this.toggleNameRelatedTooltips();
    });
  },
  methods: {
    toggleNameRelatedTooltips() {
      if (this.form.name) {
        if (this.reportTooltip) {
          this.reportTooltip.disable();
        }
        if (this.certificateTooltip) {
          this.certificateTooltip.disable();
        }
      } else {
        if (this.reportTooltip) {
          this.reportTooltip.enable();
        }
        if (this.certificateTooltip) {
          this.certificateTooltip.enable();
        }
      }
    },
    newLocation(location, index) {
      this.form.dustWipeFigures[index+1].location = location;
    },
    setNumberOfBedrooms() {
      this.numberOfBedrooms = (this.form.dustWipeFigures.length - this.threshold) / 2;
      if (this.numberOfBedrooms <= 0) {
        this.renderNumberOfBedrooms = null;
      } else {
        if (this.renderNumberOfBedrooms !== this.numberOfBedrooms) {
          this.renderNumberOfBedrooms = String(this.numberOfBedrooms);
        }
      }
    },
    getCertificateFileName(certificate) {
      if (certificate.certificateAwsFileName) {
        return certificate.certificateAwsFileName;
      }
      // if (Array.isArray(this.form.certificates) && this.form.certificates.length) {
      //   return this.form.certificates[0].certificateAwsFileName || null;
      // }
      return null;
    },

    // This is a mess, it used to be a sigle parameter, now it is an object with a file name parameter
    // Therefore I need to create this controller to fix the changes
    setCertificateFileName(value, indx) {
      if (!value) {
        this.form.certificates.splice(indx, 1);
      } else {
        this.form.certificates[indx].certificateAwsFileName = value;
      }
    },
    // Request controller for submitting file component
    requestReportController(File) {
      return {
        ReportFile: File,
        unit: this.form.name,
      };
    },
    responseReportController(response) {
      return response.data.reportFileName;
    },
    requestCertificateController(File) {
      return {
        CertificateFile: File,
        unit: this.form.name,
      };
    },
    responseCertificateController(response) {
      return response.data.certificateFileName;
    },
    removeParametersController(response) {
      return {
        fileName: response.reportFileName || response.certificateFileName,
      };
    },
    async removeGuard() {
      return new Promise((resolve) => {
        this.$modal.show('custom-dialog', {
          title: this.$wording.home.title_5,
          text: this.$wording.certify.text_12,
          buttons: [
            {
              title: 'Yes',
              class: 'yes-button',
              handler: () => {
                this.$modal.hide('custom-dialog');
                resolve(true);
              },
            },
            {
              title: 'NO',
              default: true,
              class: 'is-dark no-button',
              handler: () => {
                this.$modal.hide('custom-dialog');
                resolve(false);
              },
            },
          ],
        });
      });
    },
    // remove() {
    //   if (!this.form.dustWipeFigures) {
    //     return;
    //   }
    //   this.$modal.show('custom-dialog', {
    //     title: this.$wording.home.title_5,
    //     text: this.$wording.home.remove_figure,
    //     buttons: [
    //       {
    //         title: 'Yes',
    //         class: 'yes-button',
    //         handler: () => {
    //           this.form.dustWipeFigures.splice(this.form.dustWipeFigures.length - 2, 2);
    //           this.setNumberOfBedrooms();
    //           this.$modal.hide('custom-dialog');
    //         },
    //       },
    //       {
    //         title: 'NO',
    //         default: true,
    //         class: 'is-dark no-button',
    //         handler: () => {
    //           this.$modal.hide('custom-dialog');
    //         },
    //       },
    //     ],
    //   });
    // },
    addRoom(location) {
      if (!this.form.dustWipeFigures) {
        return;
      }
      let roomNumber = (this.form.dustWipeFigures.length - 1) / 2;
      this.form.dustWipeFigures.push({
        type: 'floor',
        location,
        value: null,
        roomNumber,
      });
      this.form.dustWipeFigures.push({
        type: 'window',
        location,
        value: null,
        roomNumber,
      });
    },

    async uploadFiles() {
      let awaitPromises = [];
      this.uploadingFiles = true;
      try {

        if (this.reportFile) {
          awaitPromises.push(this.$refs['upload-report'].upload(this.reportFile));
        }
        if (this.certificateFile) {
          if (!Array.isArray(this.form.certificates)) {
            this.form.certificates = [];
          }
          this.form.certificates.push({ certificateAwsFileName: null, certificateDownloadUrl: null });
          await this.$nextTick();
          let certLastIndex = this.form.certificates.length - 1;
          awaitPromises.push(this.$refs[`upload-certificate`][certLastIndex].upload(this.certificateFile));
        }

        return await Promise.all(awaitPromises)
          .then(responseArray => {
            this.certificateFile = null;
            if (responseArray.some(el => !el)) {
              return false;
            }
            this.uploadingFiles = false;
            this.reportFile = null;
            return true;
          })
          .catch(err => {
            console.log(err);
            this.certificateFile = null;
            this.uploadingFiles = false;
            return false;
          });
      } catch (err) {
        this.$warning('Something happened uploading the files, please check your data and try again.');
        return false;
      }
    },

    async isValid(awaitUpload) {
      if (this.form.inspectionType === 'LeadFree') {
        this.form.dustWipeFigures = [];

        // Here comes another validation 'Lead Free doesn't have Accreditation and Lab'
        this.form.labAccreditationName = 'N/A';
        this.form.labAccreditationNumber = 'N/A';
      }

      this.isValidPromise = await this.$refs['inspection-form'].validate();

      // Execute upload here
      if (this.isValidPromise) {
        if (awaitUpload) {
          this.isValidPromise = this.uploadFiles();
        } else {
          this.uploadFiles();
        }
      }

      return this.isValidPromise;
    },
  },
};
</script>

<style lang="scss" scoped>
#app {
  p.label {
    margin-bottom: 0.5rem;
  }
  .dropdown {
    .button.is-light{
      text-transform: none;
    }
  }
  .card {
    &.error {
      // background-color: $light-red;
      box-shadow: 0px 0px 2px $love-park-red;
      .card-header {
        background-color: #fed2d26b;
        // background-color: $love-park-red;
        * {
          color: $dark-gray;
        }
      }
    }
  }
  .number-of-bedrooms {
    width: 232px;
  }
}
</style>